import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import type { StoryElement } from '../types';
import { recordEvent } from '../repositories/storyRepository';
import { buildArticleClipboardText } from '../utils/clipboard';
import { storyShortcutAction } from '../utils/keyboard';
import { readerEvent } from '../utils/readerTelemetry';
import { pushedOutOfView } from '../utils/readingPosition';
import { isModalOpen } from '../components/ModalOverlay';
import { useIsAdminUser } from '../providers/UserProvider';
import type { UserEventContext } from '../repositories/storyRepository';

interface UseStoryKeyboardNavResult {
  visibleElements: StoryElement[];
  /** -1 while the list is unsettled and nobody has claimed a row. */
  focusedIndex: number;
  /**
   * Whether the list actually holds the keyboard. False until the reader hands
   * it over, so no row is painted as focused while typing still goes to the
   * search box (PRO-263).
   */
  isFocusActive: boolean;
  expandedIds: Set<string>;
  focusStory: (id: string) => void;
  /** Move both list focus and DOM focus to the first visible row. */
  focusTopStory: () => void;
  toggleExpanded: (id: string) => void;
  setRowRef: (i: number) => (el: HTMLElement | null) => void;
  notifyStoryUpdate: () => void;
}

/**
 * j/k/o/l/Enter/y/d/a/arrow keyboard navigation over a list of stories.
 * Keeps keyboard handling for story lists in one place.
 *
 * `resetKey` should change whenever the underlying feed/search changes; the
 * focused row and expanded rows reset when it does.
 *
 * `settled` is false while the list can still reorder (a feed, search or For
 * You while it builds). Until it settles no row rests focused, since the top
 * row could sink with the ring on it; the first key claims the top row instead
 * of the next.
 *
 * A reader list keeps the reader's story focused as rankings reorder it. When a
 * reorder pushes that row out of view, focus returns to the top row and
 * `onPushedOutOfView` runs.
 */
export function useStoryKeyboardNav(
  elements: StoryElement[],
  resetKey: string,
  eventContext: UserEventContext,
  { initiallyExpanded = false, readerFocus = false, settled = true, onPushedOutOfView }:
    { initiallyExpanded?: boolean; readerFocus?: boolean; settled?: boolean; onPushedOutOfView?: () => void } = {},
): UseStoryKeyboardNavResult {
  // Admins spend 'a' on the admin-mode toggle (issue #889); 'o' and ArrowRight
  // still expand, so nothing is lost.
  const isAdminUser = useIsAdminUser();
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [isFocusActive, setIsFocusActive] = useState(false);
  const [, forceRender] = useState(0);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => (initiallyExpanded ? new Set(elements.map((e) => e.id)) : new Set()),
  );
  const rowRefs = useRef<(HTMLElement | null)[]>([]);
  const focusedArticleIdRef = useRef<string | null>(null);
  // The keydown handler reads these through refs so the document listener is
  // not torn down and re-attached on every focus move or expand.
  const focusedIndexRef = useRef(0);
  focusedIndexRef.current = focusedIndex;
  const expandedIdsRef = useRef(expandedIds);
  expandedIdsRef.current = expandedIds;
  const isFocusActiveRef = useRef(false);
  isFocusActiveRef.current = isFocusActive;
  const settledRef = useRef(settled);
  settledRef.current = settled;
  // The focus listener reads the list through a ref: focus can move during a
  // commit (a push out of view), before an effect could re-bind it.
  const elementsRef = useRef(elements);
  elementsRef.current = elements;
  // Only a keyboard move scrolls the focused row into view. Hover focuses the
  // row under the pointer, and scrolling there slides a different row under
  // it, so the page would jump and chain along the viewport edges.
  const scrollOnFocusChangeRef = useRef(false);
  // Where the focused row sat in the page at the last commit, so a reorder
  // moving it can be told apart from the reader scrolling past it.
  const focusedPlaceRef = useRef<{ id: string; top: number } | null>(null);
  const onPushedOutOfViewRef = useRef(onPushedOutOfView);
  onPushedOutOfViewRef.current = onPushedOutOfView;

  const notifyStoryUpdate = useCallback(() => {
    forceRender((n) => n + 1);
  }, []);

  // Reset expanded rows and focus when the feed/search changes.
  useEffect(() => {
    setFocusedIndex(0);
    setIsFocusActive(false);
    focusedArticleIdRef.current = null;
    // A single-story page (the permalink) opens its row; a feed opens none.
    setExpandedIds(initiallyExpanded ? new Set(elements.map((e) => e.id)) : new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const focusStory = useCallback((id: string) => {
    const nextIndex = elements.findIndex((element) => element.id === id);
    if (nextIndex < 0) return;
    const row = rowRefs.current[nextIndex];
    if (!row) return;
    focusedArticleIdRef.current = id;
    focusedIndexRef.current = nextIndex;
    isFocusActiveRef.current = true;
    setFocusedIndex(nextIndex);
    setIsFocusActive(true);
    // Preserve native focus on links/buttons inside the clicked row, and never
    // pull focus out of a text field.
    const active = document.activeElement;
    const editing = active instanceof HTMLElement && active.closest('input, textarea, select, [contenteditable="true"]');
    if (!row.contains(active) && !editing) row.focus({ preventScroll: true });
  }, [elements]);

  /**
   * Hand the keyboard to the top row.
   *
   * Sets DOM focus, not just the list index: the shortcut handler ignores keys
   * typed with a textarea focused (`isTyping`), so a caller whose composer
   * holds focus — the search box after a run — would otherwise leave j/k
   * going into the query box.
   */
  const focusTopStory = useCallback(() => {
    const first = elements[0];
    if (!first) return;
    focusStory(first.id);
    rowRefs.current[0]?.scrollIntoView({ block: 'nearest' });
  }, [elements, focusStory]);

  // The ring follows DOM focus, including native Tab navigation and leaving
  // the results to edit a query. A remembered index alone does not own keys.
  useEffect(() => {
    const syncFocus = (target: EventTarget | null) => {
      const index = target instanceof Node
        ? rowRefs.current.findIndex(row => row?.contains(target))
        : -1;
      const active = index >= 0;
      if (readerFocus && !active) return;
      isFocusActiveRef.current = active;
      setIsFocusActive(active);
      if (active) {
        focusedArticleIdRef.current = elementsRef.current[index]?.id ?? null;
        focusedIndexRef.current = index;
        setFocusedIndex(index);
      }
    };
    const onFocusIn = (event: FocusEvent) => syncFocus(event.target);
    const onFocusOut = (event: FocusEvent) => syncFocus(event.relatedTarget);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [readerFocus]);

  // A reorder that pushes the reader's row out of view sends them back to the top row.
  useLayoutEffect(() => {
    if (!readerFocus) return;
    const id = focusedArticleIdRef.current;
    const row = id ? rowRefs.current[elements.findIndex((element) => element.id === id)] : null;
    const rect = row?.getBoundingClientRect();
    const before = focusedPlaceRef.current;
    focusedPlaceRef.current = id && rect ? { id, top: rect.top + window.scrollY } : null;
    if (!rect || !before || before.id !== id || !isFocusActiveRef.current ||
        !row?.contains(document.activeElement) || isModalOpen()) return;
    if (!pushedOutOfView(rect, rect.top + window.scrollY - before.top, window.innerHeight)) return;
    focusStory(elements[0].id);
    focusedPlaceRef.current = null;
    onPushedOutOfViewRef.current?.();
  });

  // Keep the focused row pointed at the same article as the list changes.
  useEffect(() => {
    const focusedArticleId = focusedArticleIdRef.current;
    if (!focusedArticleId) {
      setFocusedIndex((i) => Math.min(i, Math.max(elements.length - 1, 0)));
      return;
    }
    const nextIndex = elements.findIndex((element) => element.id === focusedArticleId);
    if (nextIndex >= 0) {
      setFocusedIndex(nextIndex);
      return;
    }
    setFocusedIndex((i) => {
      const next = Math.min(i, Math.max(elements.length - 1, 0));
      focusedArticleIdRef.current = elements[next]?.id ?? null;
      return next;
    });
  }, [elements]);

  // Scroll focused row into view when index changes and keep DOM focus in sync
  useEffect(() => {
    const row = rowRefs.current[focusedIndex];
    if (row) {
      if (
        document.activeElement && !row.contains(document.activeElement) &&
        rowRefs.current.some((r) => r === document.activeElement || r?.contains(document.activeElement))
      ) {
        row.focus({ preventScroll: true });
      }
      if (scrollOnFocusChangeRef.current) row.scrollIntoView({ block: 'nearest' });
    }
    scrollOnFocusChangeRef.current = false;
  }, [focusedIndex]);

  useEffect(() => {
    if (elements.length === 0) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      if (isModalOpen()) return;
      const action = storyShortcutAction(e, isTyping, isAdminUser);
      if (!action) return;
      if (!isFocusActiveRef.current && !readerFocus) {
        // The list was not holding the keyboard: the first key claims it and
        // lands on the top row rather than acting on a row nobody could see
        // was focused.
        focusTopStory();
        if (action === 'focus_next' || action === 'focus_previous') {
          e.preventDefault();
          return;
        }
      }
      if (action === 'focus_next' || action === 'focus_previous') scrollOnFocusChangeRef.current = true;
      if (readerFocus) {
        const resting = !isFocusActiveRef.current && !settledRef.current;
        const index = resting ? 0
          : action === 'focus_next' ? Math.min(focusedIndexRef.current + 1, elements.length - 1)
          : action === 'focus_previous' ? Math.max(focusedIndexRef.current - 1, 0) : focusedIndexRef.current;
        focusStory(elements[index].id);
        if (action === 'focus_next' || action === 'focus_previous') { e.preventDefault(); return; }
      }
      if (action === 'focus_next') {
        setFocusedIndex((i) => {
          const next = Math.min(i + 1, elements.length - 1);
          focusedArticleIdRef.current = elements[next]?.id ?? null;
          return next;
        });
      } else if (action === 'focus_previous') {
        setFocusedIndex((i) => {
          const next = Math.max(i - 1, 0);
          focusedArticleIdRef.current = elements[next]?.id ?? null;
          return next;
        });
      } else if (action === 'open_source') {
        const story = elements[focusedIndexRef.current];
        if (story?.url) {
          window.open(story.url, '_blank', 'noreferrer');
          recordEvent(story.id, 'click_external', {
            ...eventContext,
            destinationUrl: story.url,
          });
        }
      } else if (action === 'toggle_details') {
        e.preventDefault();
        const story = elements[focusedIndexRef.current];
        if (story) {
          if (!expandedIdsRef.current.has(story.id)) void recordEvent(story.id, 'show_summary', eventContext);
          toggleExpanded(story.id);
        }
      } else if (action === 'copy_article') {
        const story = elements[focusedIndexRef.current];
        if (story) {
          e.preventDefault();
          void navigator.clipboard.writeText(buildArticleClipboardText(story)).then(() => {
            if (readerFocus) readerEvent('story_link_copy', { story_id: story.id, method: 'keyboard', surface: eventContext.surface });
          }, () => {});
        }
      } else if (action === 'expand_details') {
        e.preventDefault();
        const story = elements[focusedIndexRef.current];
        if (!story) return;
        if (!expandedIdsRef.current.has(story.id)) void recordEvent(story.id, 'show_summary', eventContext);
        setExpandedIds((prev) => new Set(prev).add(story.id));
      } else if (action === 'collapse_details') {
        e.preventDefault();
        const story = elements[focusedIndexRef.current];
        if (!story) return;
        setExpandedIds((prev) => {
          const next = new Set(prev);
          next.delete(story.id);
          return next;
        });
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [elements, toggleExpanded, focusTopStory, focusStory, isAdminUser, eventContext, readerFocus]);

  // One stable callback per index, so a memoized row does not see a new
  // `ref` function on every parent render (issue: j/k re-rendered every row).
  const rowRefCallbacks = useRef<((el: HTMLElement | null) => void)[]>([]);
  const setRowRef = useCallback((i: number) => {
    let cb = rowRefCallbacks.current[i];
    if (!cb) {
      cb = (el: HTMLElement | null) => {
        rowRefs.current[i] = el;
      };
      rowRefCallbacks.current[i] = cb;
    }
    return cb;
  }, []);

  return {
    visibleElements: elements,
    focusedIndex: isFocusActive || settled ? focusedIndex : -1,
    isFocusActive,
    expandedIds,
    focusStory,
    focusTopStory,
    toggleExpanded,
    setRowRef,
    notifyStoryUpdate,
  };
}
